// src/pages/DashboardPage.tsx
import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Stack,
  Typography,
  CircularProgress,
  Table,
  Sheet,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Button,
  Chip,
  LinearProgress
} from '@mui/joy';
import useAuthStore from '../stores/authStore';
import { dashboardService, ProductWeightStats, DateRange, ProductWeightSummary } from '../api/dashboard';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { PAGE_TRANSLATIONS as t } from '../conf';
import { formatWeight } from '../util';

const formatThaiDate = (dateString: string | undefined) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString('th-TH', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour12: false
  });
};

const DashboardPage: React.FC = () => {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState<boolean>(true);
  const [stats, setStats] = useState<ProductWeightStats | null>(null);
  const [summary, setSummary] = useState<ProductWeightSummary | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
    endDate: format(endOfMonth(new Date()), 'yyyy-MM-dd')
  });
  const [error, setError] = useState<string | null>(null);
  const fetchInitiatedRef = useRef(false);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [statsResponse, summaryResponse] = await Promise.all([
        dashboardService.getProductWeightStats(dateRange),
        dashboardService.getProductWeightSummary(dateRange)
      ]);
      
      setStats(statsResponse.data);
      setSummary(summaryResponse.data);
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setError('Failed to load statistics. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (fetchInitiatedRef.current) {
      return;
    }
    fetchInitiatedRef.current = true;

    fetchDashboardData();
  }, []);

  const handleDateChange = (field: keyof DateRange) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateRange(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchDashboardData();
  };
  
  // Get top 5 customers and products
  const topCustomers = summary?.customerStats.slice(0, 5) || [];
  const topProducts = summary?.productStats.slice(0, 5) || [];
  
  // Find the max weight for normalizing progress bars
  const maxCustomerWeight = Math.max(...(topCustomers.map(c => c.totalWeight) || [1]));
  const maxProductWeight = Math.max(...(topProducts.map(p => p.totalWeight) || [1]));
  
  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3
          }}
        >
          <Typography level="h3">{t.DASHBOARD}</Typography>
        </Box>
        
        <Card>
          <CardContent>
            <Typography level="h4" gutterBottom>
              {t.WELCOME}{user ? `, ${user.username}` : ''}!
            </Typography>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <form onSubmit={handleSubmit}>
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <FormControl size="sm">
                  <FormLabel>{t.START_DATE}</FormLabel>
                  <Input
                    type="date"
                    value={dateRange.startDate}
                    onChange={handleDateChange('startDate')}
                  />
                </FormControl>
                <FormControl size="sm">
                  <FormLabel>{t.END_DATE}</FormLabel>
                  <Input
                    type="date"
                    value={dateRange.endDate}
                    onChange={handleDateChange('endDate')}
                  />
                </FormControl>
                <Button 
                  type="submit" 
                  sx={{ mt: 'auto', mb: 1, alignSelf: 'flex-end' }}
                >
                  {t.APPLY_FILTER}
                </Button>
              </Stack>
            </form>
          </CardContent>
        </Card>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Card color="danger">
            <CardContent>
              <Typography>{error}</Typography>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={2}>
            <Grid xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography level="h4">{t.QUICK_STATS}</Typography>
                  <Box sx={{ mt: 2 }}>
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography level="title-md">{t.TOTAL_COUNT}:</Typography>
                        <Typography level="body-lg" fontWeight="bold">{stats?.totalCount || 0}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography level="title-md">{t.TOTAL_WEIGHT}:</Typography>
                        <Typography level="body-lg" fontWeight="bold">{formatWeight(stats?.totalWeight || 0)} {t.KG}</Typography>
                      </Box>
                    </Stack>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography level="h4">{t.DAILY_STATISTICS}</Typography>
                  {stats?.dailyStats && stats.dailyStats.length > 0 ? (
                    <Sheet sx={{ mt: 2, height: 300, overflow: 'auto' }}>
                      <Table>
                        <thead>
                          <tr>
                            <th>{t.DATE}</th>
                            <th>{t.COUNT}</th>
                            <th>{t.WEIGHT_KG}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stats.dailyStats.map((day, index) => (
                            <tr key={index}>
                              <td>{formatThaiDate(day.date)}</td>
                              <td>{day.count}</td>
                              <td>{formatWeight(day.totalWeight)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Sheet>
                  ) : (
                    <Typography level="body-md" sx={{ mt: 2 }}>
                      {t.NO_DAILY_STATS}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Top Customers Section */}
            <Grid xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography level="h4" gutterBottom>
                    {t.TOP_CUSTOMERS}
                  </Typography>
                  {topCustomers.length > 0 ? (
                    <Box sx={{ mt: 2 }}>
                      <Stack spacing={2}>
                        {topCustomers.map((customer, index) => (
                          <Box key={index}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography level="body-sm">
                                {customer.customerName}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip size="sm" variant="soft">{customer.count} {t.ITEMS}</Chip>
                                <Typography level="body-sm">{formatWeight(customer.totalWeight)} {t.KG}</Typography>
                              </Box>
                            </Box>
                            <LinearProgress
                              determinate
                              value={(customer.totalWeight / maxCustomerWeight) * 100}
                              sx={{ height: 8, borderRadius: 4 }}
                            />
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  ) : (
                    <Typography level="body-md">{t.NO_CUSTOMER_DATA}</Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Top Products Section */}
            <Grid xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography level="h4" gutterBottom>
                    {t.TOP_PRODUCTS}
                  </Typography>
                  {topProducts.length > 0 ? (
                    <Box sx={{ mt: 2 }}>
                      <Stack spacing={2}>
                        {topProducts.map((product, index) => (
                          <Box key={index}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography level="body-sm">
                                {product.productName}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip size="sm" variant="soft">{product.count} {t.ITEMS}</Chip>
                                <Typography level="body-sm">{formatWeight(product.totalWeight)} {t.KG}</Typography>
                              </Box>
                            </Box>
                            <LinearProgress
                              determinate
                              color={index === 0 ? "success" : index === 1 ? "primary" : "neutral"}
                              value={(product.totalWeight / maxProductWeight) * 100}
                              sx={{ height: 8, borderRadius: 4 }}
                            />
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  ) : (
                    <Typography level="body-md">{t.NO_PRODUCT_DATA}</Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid xs={12}>
              <Card>
                <CardContent>
                  <Typography level="h4">{t.PERIOD_OVERVIEW}</Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography level="body-md">
                      {t.SHOWING_DATA_FROM} {formatThaiDate(dateRange.startDate)} {t.TO} {formatThaiDate(dateRange.endDate)}
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <Typography level="body-md">
                      {stats?.dailyStats && stats.dailyStats.length > 0 
                        ? `${stats.dailyStats.length} ${t.DAYS_OF_ACTIVITY}`
                        : t.NO_ACTIVITY}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Stack>
    </Box>
  );
};

export default DashboardPage;